import Link from '@tiptap/extension-link';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import React from 'react';

import { cn } from 'DesignSystem/utils';

import { RichEditorToolbar } from './rich-editor-toolbar';
import { richHtmlClassName } from './rich-html';

export const richEditorExtensions = [
  StarterKit.configure({
    heading: false,
    code: false,
    codeBlock: false,
    strike: false,
    blockquote: false,
    horizontalRule: false,
    link: false,
  }),
  Link.extend({ inclusive: false }).configure({
    openOnClick: false,
    autolink: true,
    defaultProtocol: 'https',
    HTMLAttributes: {
      rel: 'noopener noreferrer',
      target: '_blank',
    },
  }),
];

type RichEditorProps = {
  content?: string | null;
  onChange?: (html: string) => void;
  editable?: boolean;
  placeholder?: string;
  className?: string;
};

export function RichEditor({
  content = '',
  onChange,
  editable = true,
  placeholder = 'Write here…',
  className,
}: RichEditorProps) {
  const editor = useEditor({
    immediatelyRender: false,
    shouldRerenderOnTransaction: true,
    extensions: richEditorExtensions,
    content: content ?? '',
    editable,
    editorProps: {
      attributes: {
        class: cn('min-h-[8rem] px-3 py-3 outline-none', richHtmlClassName),
      },
    },
    onUpdate: ({ editor: nextEditor }) => {
      onChange?.(nextEditor.getHTML());
    },
  });

  React.useEffect(() => {
    if (!editor) return;
    editor.setEditable(editable);
  }, [editor, editable]);

  if (!editor) return null;

  return (
    <div className={cn('overflow-hidden rounded-md border border-zinc-200 bg-white', className)}>
      {editable ? <RichEditorToolbar editor={editor} /> : null}
      <div className="relative">
        {editor.isEmpty ? (
          <p className="pointer-events-none absolute left-3 top-3 text-sm text-zinc-400">{placeholder}</p>
        ) : null}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
